/**
 * BenefitQuiz — "Which Volunteer Benefit calls you?"
 *
 * A short 6-question quiz on /volunteer that scores the visitor's
 * responses across all 9 benefits and routes them to their best-match
 * article + suggests matching opportunities.
 *
 * Design goals:
 *   • Multi-select allowed on each question (each option adds weight to
 *     several benefits — motivations don't map 1:1 to answers).
 *   • Progress bar, animated card transitions, playful copy.
 *   • Result screen: winner card + 2 runners-up + "Read article" CTA
 *     + "Find matching opportunities" (filtered to related causes).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, RotateCcw, Sparkles, ArrowRight, Search } from "lucide-react";
import { VOLUNTEER_BENEFITS_BY_SLUG } from "@/data/volunteerBenefits";

/* ─── Quiz questions ─────────────────────────────────────────────
 * Each option contributes to one or more benefit slugs with a weight.
 * Weights are kept 1-3 so no single answer dominates.
 */
const QUESTIONS = [
  {
    id: "primary_motivation",
    prompt: "What draws you most to the idea of volunteering?",
    options: [
      { label: "Making an immediate difference for someone in need",
        weights: { "help-people": 3, "strengthen-communities": 1 } },
      { label: "Meeting new people and forming real friendships",
        weights: { "build-relationships": 3, "improve-wellbeing": 1 } },
      { label: "Growing as a person and finding meaning",
        weights: { "personal-growth": 3, "improve-wellbeing": 2 } },
      { label: "Building useful skills or experience",
        weights: { "learn-new-skills": 3, "gain-experience": 2 } },
    ],
  },
  {
    id: "life_stage",
    prompt: "Which best describes your life right now?",
    options: [
      { label: "Student / early career",
        weights: { "gain-experience": 3, "learn-new-skills": 2, "earn-recognition": 1 } },
      { label: "Working professional",
        weights: { "personal-growth": 2, "build-relationships": 2, "improve-wellbeing": 1 } },
      { label: "Parent / carer",
        weights: { "strengthen-communities": 3, "help-people": 2 } },
      { label: "Retired or semi-retired",
        weights: { "improve-wellbeing": 3, "build-relationships": 2, "strengthen-communities": 1 } },
    ],
  },
  {
    id: "energy",
    prompt: "How do you feel most days?",
    options: [
      { label: "Fine, but disconnected from other people",
        weights: { "build-relationships": 3, "improve-wellbeing": 2 } },
      { label: "A bit anxious or restless — I need purpose",
        weights: { "improve-wellbeing": 3, "personal-growth": 2 } },
      { label: "Full of energy, want to channel it",
        weights: { "help-people": 2, "strengthen-communities": 2 } },
      { label: "Focused on my career and growth",
        weights: { "gain-experience": 3, "learn-new-skills": 2, "earn-recognition": 1 } },
    ],
  },
  {
    id: "commitment",
    prompt: "How much time can you realistically give?",
    options: [
      { label: "A couple of hours occasionally",
        weights: { "help-people": 2, "improve-wellbeing": 2 } },
      { label: "A few hours every week",
        weights: { "build-relationships": 2, "strengthen-communities": 2, "learn-new-skills": 1 } },
      { label: "A regular multi-hour weekly commitment",
        weights: { "gain-experience": 3, "personal-growth": 2, "volunteer-awards": 1 } },
      { label: "Whenever there's a real crisis nearby",
        weights: { "help-people": 3, "strengthen-communities": 2 } },
    ],
  },
  {
    id: "outcome",
    prompt: "What would make your volunteering feel like a win?",
    options: [
      { label: "Someone whose life visibly changed",
        weights: { "help-people": 3, "strengthen-communities": 1 } },
      { label: "A skill or certification I can put on my résumé",
        weights: { "learn-new-skills": 3, "gain-experience": 2 } },
      { label: "Feeling healthier, calmer, more connected",
        weights: { "improve-wellbeing": 3, "personal-growth": 2 } },
      { label: "Being publicly recognised or awarded",
        weights: { "earn-recognition": 3, "volunteer-awards": 2 } },
    ],
  },
  {
    id: "style",
    prompt: "Pick the phrase that sounds most like you.",
    options: [
      { label: "\"I want to be part of something bigger.\"",
        weights: { "strengthen-communities": 3, "build-relationships": 1 } },
      { label: "\"I learn best by doing.\"",
        weights: { "learn-new-skills": 3, "gain-experience": 2 } },
      { label: "\"I want to become the person I could be.\"",
        weights: { "personal-growth": 3, "improve-wellbeing": 1 } },
      { label: "\"I show up when it matters.\"",
        weights: { "help-people": 3, "volunteer-awards": 1 } },
    ],
  },
];

/* ─── Component ───────────────────────────────────────────────── */
export default function BenefitQuiz() {
  const [step, setStep]        = useState(0);
  const [answers, setAnswers]  = useState({}); // { qid: Set<optionIdx> }
  const [done, setDone]        = useState(false);

  const q = QUESTIONS[step];
  const selectedSet = answers[q?.id] || new Set();

  const toggleOption = (idx) => {
    setAnswers((prev) => {
      const next = { ...prev };
      const set = new Set(next[q.id] || []);
      if (set.has(idx)) set.delete(idx); else set.add(idx);
      next[q.id] = set;
      return next;
    });
  };

  const advance = () => {
    if (step < QUESTIONS.length - 1) setStep(step + 1);
    else setDone(true);
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setDone(false);
  };

  const ranked = useMemo(() => {
    if (!done) return [];
    const scores = {};
    for (const q of QUESTIONS) {
      const picks = answers[q.id];
      if (!picks) continue;
      for (const idx of picks) {
        const opt = q.options[idx];
        for (const [slug, w] of Object.entries(opt.weights || {})) {
          scores[slug] = (scores[slug] || 0) + w;
        }
      }
    }
    return Object.entries(scores)
      .map(([slug, score]) => ({ slug, score, article: VOLUNTEER_BENEFITS_BY_SLUG[slug] }))
      .filter((r) => r.article)
      .sort((a, b) => b.score - a.score);
  }, [done, answers]);

  const winner = ranked[0]?.article;
  const runners = ranked.slice(1, 3).map((r) => r.article);
  const maxScore = ranked[0]?.score || 1;

  /* ─── Result screen ──────────────────────────────────────── */
  if (done && winner) {
    const c = winner.color;
    return (
      <section
        className="mt-10 rounded-3xl border overflow-hidden"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
        data-testid="volunteer-quiz-result"
      >
        {/* Winner banner */}
        <div
          className="relative p-6 sm:p-8"
          style={{ background: `linear-gradient(135deg, ${c} 0%, ${c}bb 60%, ${c}66 100%)`, color: "white" }}
        >
          <div className="flex items-center gap-2 text-white/90 text-xs font-mono uppercase tracking-widest">
            <Sparkles className="w-4 h-4" /> Your Volunteer Benefit
          </div>
          <div className="mt-2 flex items-center gap-4">
            <div className="text-5xl">{winner.emoji}</div>
            <div>
              <div className="font-display text-3xl sm:text-4xl leading-tight">{winner.title}</div>
              <div className="mt-1 text-white/90 max-w-2xl">{winner.tagline}</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to={`/volunteer/benefits/${winner.slug}`}
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-4 py-2 text-sm font-medium hover:-translate-y-0.5 transition-transform"
              data-testid="volunteer-quiz-read-winner"
            >
              Read the full article <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/opportunities"
              className="inline-flex items-center gap-2 rounded-full border border-white/50 px-4 py-2 text-sm font-medium hover:bg-white/10"
              data-testid="volunteer-quiz-find-opps"
            >
              <Search className="w-4 h-4" /> Find matching opportunities
            </Link>
            <button
              onClick={reset}
              className="inline-flex items-center gap-2 rounded-full border border-white/50 px-4 py-2 text-sm font-medium hover:bg-white/10"
              data-testid="volunteer-quiz-restart"
            >
              <RotateCcw className="w-4 h-4" /> Take again
            </button>
          </div>
        </div>

        {/* Runners-up */}
        {runners.length > 0 && (
          <div className="p-6 sm:p-8">
            <div className="overline mb-3">You also lean toward…</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {runners.map((r) => (
                <Link
                  key={r.slug}
                  to={`/volunteer/benefits/${r.slug}`}
                  className="rounded-2xl border p-4 flex items-start gap-3 hover:-translate-y-0.5 transition-transform"
                  style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
                  data-testid={`volunteer-quiz-runner-${r.slug}`}
                >
                  <div className="text-3xl">{r.emoji}</div>
                  <div>
                    <div className="font-display text-lg">{r.title}</div>
                    <div className="text-xs opacity-70 mt-0.5">{r.tagline}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 ml-auto opacity-50 self-center" />
                </Link>
              ))}
            </div>

            {/* Score reveal (top 5) — a small transparency touch */}
            <div className="mt-6">
              <div className="overline mb-2">How your answers scored</div>
              <div className="space-y-1.5">
                {ranked.slice(0, 5).map((r) => (
                  <div key={r.slug} className="flex items-center gap-3 text-xs">
                    <div className="w-40 shrink-0 flex items-center gap-1.5">
                      <span>{r.article.emoji}</span>
                      <span>{r.article.title}</span>
                    </div>
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--wofy-border)" }}>
                      <div className="h-full rounded-full" style={{ width: `${(r.score / maxScore) * 100}%`, background: r.article.color }} />
                    </div>
                    <div className="w-8 text-right font-mono opacity-70">{r.score}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }

  /* ─── Question screen ────────────────────────────────────── */
  const progress = ((step + (selectedSet.size ? 0.5 : 0)) / QUESTIONS.length) * 100;
  const canAdvance = selectedSet.size > 0;

  return (
    <section
      className="mt-10 rounded-3xl border overflow-hidden"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
      data-testid="volunteer-quiz"
    >
      {/* Header + progress */}
      <div className="p-5 sm:p-6 border-b" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>Which Volunteer Benefit Calls You?</div>
            <div className="font-display text-xl mt-1">A 60-second quiz to find your best match</div>
          </div>
          <div className="text-xs font-mono opacity-70">
            {step + 1} / {QUESTIONS.length}
          </div>
        </div>
        <div className="mt-3 h-1 rounded-full overflow-hidden" style={{ background: "var(--wofy-border)" }}>
          <div
            className="h-full transition-all"
            style={{ width: `${progress}%`, background: "var(--wofy-primary)" }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="p-5 sm:p-6">
        <div className="font-display text-2xl leading-snug mb-1">{q.prompt}</div>
        <div className="text-xs opacity-60 mb-4">Pick one or more — nothing is off-limits.</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {q.options.map((opt, idx) => {
            const active = selectedSet.has(idx);
            return (
              <button
                key={idx}
                onClick={() => toggleOption(idx)}
                className="text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5"
                style={{
                  borderColor: active ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background: active ? "var(--wofy-surface)" : "var(--wofy-bg)",
                  boxShadow: active ? "0 0 0 2px var(--wofy-primary-tint, rgba(0,0,0,0.05))" : "none",
                }}
                data-testid={`volunteer-quiz-option-${q.id}-${idx}`}
              >
                <div className="flex items-start gap-2">
                  <div
                    className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0"
                    style={{
                      borderColor: active ? "var(--wofy-primary)" : "var(--wofy-border)",
                      background: active ? "var(--wofy-primary)" : "transparent",
                    }}
                    aria-hidden
                  >
                    {active && <span className="text-white text-[10px] leading-none">✓</span>}
                  </div>
                  <div className="text-sm">{opt.label}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-between">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="text-xs font-mono uppercase tracking-widest opacity-70 hover:opacity-100"
              data-testid="volunteer-quiz-back"
            >
              ← Back
            </button>
          ) : <span />}
          <button
            onClick={advance}
            disabled={!canAdvance}
            className="wofy-btn-primary text-sm"
            style={{ opacity: canAdvance ? 1 : 0.5, cursor: canAdvance ? "pointer" : "not-allowed" }}
            data-testid="volunteer-quiz-next"
          >
            {step === QUESTIONS.length - 1 ? "See my match" : "Next question"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
